import { Tooltip } from "@material-ui/core";
import { CloudUpload, GetApp, Person, School } from "@material-ui/icons";
import "moment/locale/de-ch";
import * as React from "react";
import { WithIntlProps, injectIntl } from "react-intl";
import * as CandidateApi from "../../api/CandidateApi";
import { SwissGermanLocale, SwissTimeZone } from "../../data/constants";
import { CandidateViewModel } from "../../model/CandidateViewModel";
import { PersonType } from "../../model/PersonType";
import { GetTranslation } from "../../translations/GetTranslation";
import CustomButton from "../common/buttons/CustomButton";
import { ColumnDefinition, CustomGrid, CustomGridFilter } from "../common/customGrid/CustomGrid";
import TranslatedMessage from "../translations/TranslatedMessage";

type ICandidateGridProps = {
  candidates: CandidateViewModel[];
  onCandidateClick: (id: string) => void;
  onAddClick: (event: any) => void;
  onUploadEdiIdsButtonClick: () => void;
} & WithIntlProps<any>;

type CandidateGridState = {
  filters: CustomGridFilter<CandidateViewModel>[];
};

class CandidateGrid extends React.Component<ICandidateGridProps, CandidateGridState> {
  constructor(props: any) {
    super(props);

    const { intl } = props;
    const filters: CustomGridFilter<CandidateViewModel>[] = [
      {
        label: GetTranslation(intl, "person.field.showOnlyExperts"),
        isActive: false,
        filterFunction: (row: CandidateViewModel) => row.type === PersonType.Expert,
        onFilterChange: this.getHandlerByFilterIndex(0),
      },
    ];

    this.state = {
      filters,
    };
  }

  private columnsDefinitions: Array<ColumnDefinition<CandidateViewModel>> = [
    {
      columnKey: "icon",
      columnLabel: <TranslatedMessage id="person.field.icon" />,
      orderValue: (_) => _.type,
      cellValue: (_) => {
        if (_.type === PersonType.Expert) {
          return (
            <Tooltip title={<TranslatedMessage id="expert.header" />}>
              <Person />
            </Tooltip>
          );
        }
        return (
          <Tooltip title={<TranslatedMessage id="candidate.header" />}>
            <School />
          </Tooltip>
        );
      },
    },
    {
      columnKey: "firstName",
      columnLabel: <TranslatedMessage id="person.field.firstName" />,
      cellValue: (_) => _.firstName,
    },
    {
      columnKey: "lastName",
      columnLabel: <TranslatedMessage id="person.field.lastName" />,
      cellValue: (_) => _.lastName,
    },
    { columnKey: "street", columnLabel: <TranslatedMessage id="person.field.street" />, cellValue: (_) => _.street },
    { columnKey: "zipCode", columnLabel: <TranslatedMessage id="person.field.zipCode" />, cellValue: (_) => _.zipCode },
    { columnKey: "city", columnLabel: <TranslatedMessage id="person.field.city" />, cellValue: (_) => _.city },
    {
      columnKey: "email",
      columnLabel: <TranslatedMessage id="person.field.email" />,
      cellValue: (_) => _.emailAddress,
    },
    {
      columnKey: "mobileNumber",
      columnLabel: <TranslatedMessage id="person.field.mobileNumber" />,
      cellValue: (_) => _.mobileNumber,
    },
    {
      columnKey: "birthdate",
      columnLabel: <TranslatedMessage id="person.field.birthDate" />,
      orderValue: (_) => _.birthdate,
      cellValue: (_) => new Date(_.birthdate).toLocaleDateString(SwissGermanLocale, { timeZone: SwissTimeZone }),
    },
  ];

  private additionalButtons = (
    <React.Fragment>
      <CustomButton
        color="primary"
        tooltip={<TranslatedMessage id="candidate.button.exportAll" />}
        onClick={() => {
          CandidateApi.exportAllCandidates();
        }}
      >
        <GetApp />
      </CustomButton>
      <CustomButton
        color="primary"
        tooltip={<TranslatedMessage id="candidate.button.uploadEdiIds" />}
        onClick={this.props.onUploadEdiIdsButtonClick}
      >
        <CloudUpload />
      </CustomButton>
    </React.Fragment>
  );

  private getHandlerByFilterIndex(index: number) {
    return (checked: boolean) => {
      this.setState((prevState) => {
        const newFilters = [...prevState.filters];
        newFilters[index].isActive = checked;
        return {
          ...prevState,
          filters: [...newFilters],
        };
      });
    };
  }

  public render() {
    return (
      <CustomGrid<CandidateViewModel>
        filters={this.state.filters}
        columnDefinitions={this.columnsDefinitions}
        rows={this.props.candidates}
        onRowClick={(_) => this.props.onCandidateClick(_.id)}
        rowKey={(_) => _.id}
        addButtonTooltip={<TranslatedMessage id="candidate.button.new" />}
        onAddClick={this.props.onAddClick}
        additionalButtons={
          this.props.candidates.length > 0 ? this.additionalButtons : <React.Fragment></React.Fragment>
        }
      />
    );
  }
}

export default injectIntl(CandidateGrid);
